import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import { ActionType, getType } from "typesafe-actions";
import { recordsActions } from "../actions";
import { getProductPageProductID } from "../../productPage/selectors";
import createFuturesRecord from "./utils/createFuturesRecord";
import dataAPI from "../../../services/dataServices";
import mapFuturesRecordsDtoToFuturesRecords from "./utils/mapFuturesRecordsDtoToFuturesRecords";
import { AxiosResponse } from "axios";
import {
  FuturesRecordDto,
  TFutureRecord,
} from "../../../types/dto/FuturesRecordDto";
import { FuturesRaw, FuturesRecords } from "../../../types/FuturesRecord";
import { toast } from "react-toastify";
import { productPageActions } from "../../productPage/actions";
import { dateService } from "../../../services/date";
import {
  addYears,
  endOfMonth,
  format,
  isSameDay,
  startOfMonth,
  subDays,
} from "date-fns";

const ODATA_4_PAGE = 10000;

function* initializeFuturesRecords() {
  try {
    const productID: number | null = yield select(getProductPageProductID);
    const strProductID = productID ? String(productID) : "";
    const today = dateService.today();
    const from = dateService.formatForApi(dateService.startOf(today, "month"));
    const to = dateService.formatForApi(
      dateService.add(dateService.startOf(today, "month"), 2, "year")
    );
    const toTradingDate = dateService.formatForApi(today);
    const fromTradingDate = dateService.formatForApi(
      dateService.subtract(today, 2, "month")
    );
    const fetchedFutureRecords: Array<TFutureRecord> = yield call(
      getAllFutureRecordsWithTradeDate,
      { from, fromTradingDate, strProductID, to, toTradingDate }
    );
    const futuresRecords: Array<FuturesRaw> = yield mapFuturesRecordsDtoToFuturesRecords(
      fetchedFutureRecords
    );
    const latestTradingDate =
      fetchedFutureRecords[fetchedFutureRecords.length - 1].ReportPeriod;
    yield put(
      productPageActions.setFuturesTradingDate(new Date(latestTradingDate))
    );
    const records: FuturesRecords = yield call(
      createFuturesRecord,
      futuresRecords
    );
    yield put(
      recordsActions.futures.setFutures(strProductID, {
        recordsData: records,
        rawRecords: futuresRecords,
      })
    );
  } catch (e) {
    console.error(e);
  }
}

function* fetchFuturesRecords(
  action: ActionType<typeof recordsActions.futures.fetchFuturesByReportPeriod>
) {
  try {
    const productID: number | null = yield select(getProductPageProductID);
    const strProductID = productID ? String(productID) : "";
    const toTradingDate = format(action.payload, "yyyy-MM-dd");
    const fromTradingDate = format(subDays(action.payload, 35), "yyyy-MM-dd");
    const date = new Date(Date.now());
    const from = format(new Date(), "yyyy-MM-dd");
    const to = format(
      new Date(date.getFullYear() + 2, date.getMonth()),
      "yyyy-MM-dd"
    );
    const fetchedFutureRecords: Array<TFutureRecord> = yield call(
      getAllFutureRecordsWithTradeDate,
      { from, fromTradingDate, strProductID, to, toTradingDate }
    );
    const toTradingDateFutures = fetchedFutureRecords.filter((ffr) =>
      isSameDay(new Date(ffr.ReportPeriod), new Date(toTradingDate))
    );
    if (toTradingDateFutures.length !== 0) {
      yield put(
        productPageActions.setFuturesTradingDate(
          new Date(action.payload.toString() + "z")
        )
      );
      const futuresRecords: Array<FuturesRaw> = yield mapFuturesRecordsDtoToFuturesRecords(
        fetchedFutureRecords
      );
      const records: FuturesRecords = yield call(
        createFuturesRecord,
        futuresRecords
      );
      yield put(
        recordsActions.futures.setFutures(strProductID, {
          recordsData: records,
          rawRecords: futuresRecords,
        })
      );
    } else {
      const records: FuturesRecords = yield call(createFuturesRecord, []);
      yield put(
        recordsActions.futures.setFutures(strProductID, {
          recordsData: records,
          rawRecords: [],
        })
      );
      toast.error(
        `Trade date ${format(
          new Date(toTradingDate),
          "MMM dd, yyyy"
        )} is not available.`
      );
    }
  } catch (e) {
    console.error(e);
  }
}

function* fetchFuturesByContractMonth(
  action: ActionType<typeof recordsActions.futures.fetchFuturesByContractMonth>
) {
  const productID: number | null = yield select(getProductPageProductID);
  const strProductID = productID ? String(productID) : "";
  const toContractMonth = format(endOfMonth(action.payload), "yyyy-MM-dd");
  const fromContractMonth = format(startOfMonth(action.payload), "yyyy-MM-dd");
  try {
    const fetchedFutureRecords: Array<TFutureRecord> = [];
    let hasNext = true;
    let iteration = 0;
    do {
      const skipFor = iteration !== 0 ? iteration * ODATA_4_PAGE : undefined;
      const futuresRecordsDto: AxiosResponse<FuturesRecordDto> = yield call(
        dataAPI.fetchFuturesRecordWithContractMonth,
        {
          from: fromContractMonth,
          productID: strProductID,
          to: toContractMonth,
          skipFor,
        }
      );
      futuresRecordsDto.data.value.forEach((futureRec) => {
        fetchedFutureRecords.push({ ...futureRec });
      });
      iteration++;
      hasNext = !!futuresRecordsDto.data["@odata.nextLink"];
    } while (hasNext);
    const futuresRecords: Array<FuturesRaw> = yield mapFuturesRecordsDtoToFuturesRecords(
      fetchedFutureRecords
    );
    const records: FuturesRecords = yield call(
      createFuturesRecord,
      futuresRecords
    );
    yield put(
      recordsActions.futures.setFutures(strProductID, {
        recordsData: records,
        rawRecords: futuresRecords,
      })
    );
    yield put(
      productPageActions.setFuturesContractMonth(endOfMonth(action.payload))
    );
  } catch (e) {
    toast.error(
      `Trade date ${format(action.payload, "MMM dd, yyyy")} is not available.`
    );
    console.error(e);
  }
}

function* fetchFuturesByNearbyIndex(
  action: ActionType<typeof recordsActions.futures.fetchFuturesByNearbyIndex>
) {
  try {
    const productID: number | null = yield select(getProductPageProductID);
    const strProductID = productID ? String(productID) : "";
    const date = new Date(Date.now());
    const from = format(date, "yyyy-MM-dd");
    const to = format(addYears(date, 2), "yyyy-MM-dd");
    const fetchedFutureRecords: Array<TFutureRecord> = [];
    let hasNext = true;
    let iteration = 0;
    do {
      const skipFor = iteration !== 0 ? iteration * ODATA_4_PAGE : undefined;
      const futuresRecordsDto: AxiosResponse<FuturesRecordDto> = yield call(
        dataAPI.fetchFuturesRecordWithNearbyIndex,
        { from, index: action.payload, productID: strProductID, to, skipFor }
      );
      futuresRecordsDto.data.value.forEach((futureRec) => {
        fetchedFutureRecords.push({ ...futureRec });
      });
      iteration++;
      hasNext = !!futuresRecordsDto.data["@odata.nextLink"];
    } while (hasNext);
    const futuresRecords: Array<FuturesRaw> = yield mapFuturesRecordsDtoToFuturesRecords(
      fetchedFutureRecords
    );
    const records: FuturesRecords = yield call(
      createFuturesRecord,
      futuresRecords
    );
    yield put(
      recordsActions.futures.setFutures(strProductID, {
        recordsData: records,
        rawRecords: futuresRecords,
      })
    );
    yield put(productPageActions.setFuturesNearbyIndex(action.payload));
  } catch (e) {
    console.error(e);
  }
}

function* futuresRecordWatcher() {
  yield all([
    takeEvery(
      getType(recordsActions.futures.initializeFutures),
      initializeFuturesRecords
    ),
    takeLatest(
      getType(recordsActions.futures.fetchFuturesByReportPeriod),
      fetchFuturesRecords
    ),
    takeLatest(
      getType(recordsActions.futures.fetchFuturesByContractMonth),
      fetchFuturesByContractMonth
    ),
    takeLatest(
      getType(recordsActions.futures.fetchFuturesByNearbyIndex),
      fetchFuturesByNearbyIndex
    ),
  ]);
}

export default futuresRecordWatcher;

function* getAllFutureRecordsWithTradeDate({
  from,
  fromTradingDate,
  strProductID,
  to,
  toTradingDate,
}: {
  from: string;
  fromTradingDate: string;
  strProductID: string;
  to: string;
  toTradingDate: string;
}) {
  const fetchedFutureRecords: Array<TFutureRecord> = [];
  let hasNext = true;
  let iteration = 0;
  do {
    const skipFor = iteration !== 0 ? iteration * ODATA_4_PAGE : undefined;
    const futuresRecordsDto: AxiosResponse<FuturesRecordDto> = yield call(
      dataAPI.fetchFuturesRecordWithTradeDate,
      {
        from,
        fromTradingDate,
        productID: strProductID,
        to,
        toTradingDate,
        skipFor,
      }
    );
    futuresRecordsDto.data.value.forEach((futureRec) => {
      fetchedFutureRecords.push({ ...futureRec });
    });
    iteration++;
    hasNext = !!futuresRecordsDto.data["@odata.nextLink"];
  } while (hasNext);
  return fetchedFutureRecords;
}
